import { useEffect, useState } from "react";
import type { MouseEvent } from "react";
import type {
  AmplitudeClientLike,
  IrisEnvironment,
  LaunchDarklyClientLike,
} from "./irisCtaRuntime";
import {
  getInitialIMessageUrl,
  isIOS,
  resolveIMessageUrl,
  trackIrisCtaClickWithOptions,
} from "./irisCtaRuntime";
import { PillLink } from "./PillLink";
import { useAmplitude } from "../lib/AmplitudeProvider";

type IrisCtaLinkProps = {
  // Same value as Amplitude's device id.
  deviceId?: string;
  ldClient?: LaunchDarklyClientLike;
  amplitudeClient?: AmplitudeClientLike;
  environment?: IrisEnvironment;
  overridePhone?: string;
  overrideText?: string;
  theme?: "light" | "dark";
  className?: string;
  showIcon?: boolean;
  label?: string;
  // When provided, bypasses the internal URL resolution and uses this href directly
  overrideHref?: string;
};

export function IrisCtaLink({
  deviceId,
  ldClient,
  amplitudeClient: amplitudeClientProp,
  environment,
  overridePhone,
  overrideText,
  theme = "light",
  className,
  showIcon = true,
  label,
  overrideHref,
}: IrisCtaLinkProps) {
  const resolvedLabel = label ?? (isIOS() ? "iMessage Iris" : "Text Iris");
  const amplitudeFromContext = useAmplitude();
  const amplitudeClient = amplitudeClientProp ?? amplitudeFromContext;

  const [href, setHref] = useState(
    () =>
      overrideHref ??
      getInitialIMessageUrl({
        phoneNumber: overridePhone,
        message: overrideText,
      }),
  );

  useEffect(() => {
    // Skip URL resolution if overrideHref is provided
    if (overrideHref) {
      setHref(overrideHref);
      return;
    }

    let isMounted = true;

    resolveIMessageUrl({
      deviceId,
      ldClient,
      environment,
      phoneNumber: overridePhone,
      message: overrideText,
    }).then((resolvedHref) => {
      if (isMounted) {
        setHref(resolvedHref);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [
    deviceId,
    ldClient,
    environment,
    overridePhone,
    overrideText,
    overrideHref,
  ]);

  const handleCtaClick = async (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    // Use overrideHref directly if provided
    const resolvedHref =
      overrideHref ??
      (await resolveIMessageUrl({
        deviceId,
        ldClient,
        environment,
        phoneNumber: overridePhone,
        message: overrideText,
      }));
    setHref(resolvedHref);

    await trackIrisCtaClickWithOptions({
      source: "webflow",
      deviceId,
      theme,
      environment,
      amplitudeClient: amplitudeClient ?? undefined,
    });
    window.location.href = resolvedHref;
  };

  return (
    <PillLink
      id="iris-imessage-cta"
      href={href}
      onClick={handleCtaClick}
      theme={theme}
      className={`iris-cta-link ${className}`.trim()}
      data-iris-imessage-cta=""
    >
      {showIcon && (
        <svg
          className="iris-imessage-icon"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 3C6.477 3 2 6.94 2 11.8c0 2.676 1.38 5.073 3.559 6.68L4.8 21l3.18-1.74c1.215.34 2.548.54 4.02.54 5.523 0 10-3.94 10-8.8S17.523 3 12 3Z"
            fill="#6E6E6E"
          />
        </svg>
      )}
      <span className="iris-cta-label">{resolvedLabel}</span>
    </PillLink>
  );
}
